import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Users, CurrencyInr, LockKey, Bank, TrendUp, CaretRight } from "@phosphor-icons/react";


const fmtInt = (n) => (n || 0).toLocaleString("en-IN");
const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const TONE = {
  slate: "text-slate-500 bg-slate-100",
  blue: "text-blue-600 bg-blue-50",
  emerald: "text-emerald-600 bg-emerald-50",
  amber: "text-amber-600 bg-amber-50",
  rose: "text-rose-600 bg-rose-50",
  violet: "text-violet-600 bg-violet-50",
};

function Stat({ label, value, icon: Icon, hint, tone = "slate", testid }) {
  return (
    <Card className="p-5 border border-slate-200 shadow-none hover:shadow-sm transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
          <div className="stat-num text-3xl font-bold text-slate-900 mt-2">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${TONE[tone]}`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
    </Card>
  );
}

const PIE_COLORS = ["#2563EB", "#7C3AED", "#F59E0B", "#10B981", "#EF4444", "#0EA5E9", "#64748B", "#EC4899", "#14B8A6"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("Active");
  const navigate = useNavigate();
  const isExec = !!(user && user.role !== "admin");

  useEffect(() => {
    if (isExec) return;
    (async () => {
      try {
        const [{ data: sum }, { data: list }] = await Promise.all([
          api.get("/analytics/summary"),
          api.get("/campaigns"),
        ]);
        setData(sum);
        setCampaigns(list);
      } finally { setLoading(false); }
    })();
  }, [isExec]);

  const filteredCampaigns = useMemo(() => {
    if (statusFilter === "all") return campaigns;
    return campaigns.filter((c) => (c.status || "Active") === statusFilter);
  }, [campaigns, statusFilter]);

  // Executives don't see the dashboard — send them to /campaigns
  if (isExec) {
    return <Navigate to="/campaigns" replace />;
  }

  if (loading || !data) return <div className="text-slate-500 text-sm" data-testid="dashboard-loading">Loading analytics…</div>;

  const statusColor = (s) => ({
    Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
    Completed: "bg-slate-100 text-slate-700 border-slate-200",
  }[s] || "bg-slate-100 text-slate-700 border-slate-200");

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Overview</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Every campaign, every creator, every rupee — in one glance.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-5 border border-slate-200 shadow-none" data-testid="stat-campaigns-combined">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Campaigns</div>
              <div className="stat-num text-xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2">{fmtInt(data.total_campaigns)}</div>
              <div className="mt-2 sm:mt-3 grid grid-cols-3 gap-1 sm:gap-2 text-[10px] sm:text-[11px]">
                <div>
                  <div className="stat-num text-xs sm:text-sm font-semibold text-emerald-700">{fmtInt(data.active_campaigns)}</div>
                  <div className="text-slate-500">Active</div>
                </div>
                <div>
                  <div className="stat-num text-xs sm:text-sm font-semibold text-amber-700">{fmtInt(data.on_hold_campaigns)}</div>
                  <div className="text-slate-500">On hold</div>
                </div>
                <div>
                  <div className="stat-num text-xs sm:text-sm font-semibold text-slate-600">{fmtInt(data.completed_campaigns)}</div>
                  <div className="text-slate-500">Completed</div>
                </div>
              </div>
            </div>
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md flex items-center justify-center text-blue-600 bg-blue-50 shrink-0">
              <Megaphone size={18} weight="duotone" />
            </div>
          </div>
        </Card>
        <Stat testid="stat-budget" label="Total Budget" value={fmtCur(data.total_commercial)} icon={Bank} tone="slate" hint="Across all campaigns" />
        <Stat testid="stat-spend" label="Allocated" value={fmtCur(data.allocated_spend)} icon={CurrencyInr} tone="violet" hint={`${fmtCur(data.total_paid)} paid`} />
        <Stat testid="stat-profit" label={Number(data.total_profit) < 0 ? "Loss" : "Profit"} value={fmtCur(Math.abs(Number(data.total_profit) || 0))} icon={TrendUp} tone={Number(data.total_profit) < 0 ? "rose" : "emerald"} hint={Number(data.total_profit) < 0 ? "Over budget" : "Budget − allocated"} />
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Card className="p-5 border border-slate-200 shadow-none" data-testid="campaigns-status-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Campaigns</div>
              <div className="font-heading text-lg font-semibold text-slate-900">By status</div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="dashboard-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filteredCampaigns.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No campaigns in this status</div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto -mx-2">
              {filteredCampaigns.map((c) => {
                const isCreatorBased = c.campaign_type === "Creator Based";
                const client = Number(c.client_cost_total || 0);
                const internal = Number(c.internal_cost_total || 0);
                const paid = Number(c.scoped_paid || 0);
                const addCostsTotal = Number(c.additional_costs_total || 0);
                const addCostsPaid = Number(c.additional_costs_paid || 0);
                const feePct = Number(c.agency_fee_pct || 0);
                const feeAmt = isCreatorBased ? client * (feePct / 100) : 0;
                const budgetDisplay = isCreatorBased ? client + feeAmt : Number(c.commercial || 0);
                const allocatedDisplay = isCreatorBased ? internal + addCostsTotal : Number(c.allocated_spend || 0);
                const pendingDisplay = isCreatorBased
                  ? Math.max((internal + addCostsTotal) - (paid + addCostsPaid), 0)
                  : Number(c.pending_payout || 0);
                const profit = isCreatorBased ? (client + feeAmt - internal - addCostsTotal) : budgetDisplay - allocatedDisplay;
                const pct = budgetDisplay > 0 ? (profit / budgetDisplay) * 100 : null;
                const positive = profit >= 0;
                const hasData = isCreatorBased ? (client > 0 || internal > 0) : (budgetDisplay > 0 || allocatedDisplay > 0);
                const tone = positive ? "text-emerald-700" : "text-rose-600";
                const label = positive ? "Profit" : "Loss";
                const tintCls = !hasData
                  ? "hover:bg-slate-50/60"
                  : positive
                    ? "bg-emerald-50/40 hover:bg-emerald-50 border-l-2 border-emerald-400"
                    : "bg-rose-50/40 hover:bg-rose-50 border-l-2 border-rose-400";
                return (
                <button
                  key={c.id}
                  onClick={(e) => { e.preventDefault(); window.open(`/campaigns/${c.id}`, "_blank", "noopener"); }}
                  onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(`/campaigns/${c.id}`, "_blank", "noopener"); } }}
                  className={`w-full text-left px-2 py-3 flex items-center justify-between rounded transition-colors ${tintCls}`}
                  data-testid={`dashboard-campaign-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.brand_name}</div>
                    {c.campaign_name && <div className="text-xs text-blue-600 truncate">{c.campaign_name}</div>}
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><span className="font-mono text-slate-900 font-semibold">{c.shared_count || 0}</span> shared</span>
                      <span><span className="font-mono text-slate-900 font-semibold">{c.locked_count || 0}</span> locked</span>
                      <span><span className="font-mono text-slate-900 font-semibold">{c.live_count || 0}</span> live</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500" data-testid={`dashboard-cost-line-${c.id}`}>
                      <span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">Budget</span>
                        <span className="font-mono font-semibold text-slate-900" data-testid={`dashboard-budget-${c.id}`}>{fmtCur(budgetDisplay)}</span>
                      </span>
                      <span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">Allocated</span>
                        <span className="font-mono font-semibold text-slate-900" data-testid={`dashboard-allocated-${c.id}`}>{fmtCur(allocatedDisplay)}</span>
                      </span>
                      <span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">Pending</span>
                        <span className="font-mono font-semibold text-rose-600" data-testid={`dashboard-pending-${c.id}`}>{fmtCur(pendingDisplay)}</span>
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500" data-testid={`dashboard-metrics-${c.id}`}>
                      <span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">{label}</span>
                        <span className={`font-mono font-semibold ${tone}`} data-testid={`dashboard-profit-${c.id}`}>{fmtCur(Math.abs(profit))}</span>
                      </span>
                      <span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">{label} %</span>
                        <span className={`font-mono font-semibold ${pct === null ? "text-slate-400" : tone}`} data-testid={`dashboard-profit-pct-${c.id}`}>{pct === null ? "—" : `${Math.abs(pct).toFixed(1)}%`}</span>
                      </span>
                    </div>
                    {(c.assigned_users || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {c.assigned_users.slice(0, 4).map((u) => (
                          <span
                            key={u.id}
                            className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 text-[10px]"
                            title={u.email}
                          >
                            <span className="w-3.5 h-3.5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[8px] font-semibold">
                              {(u.name || "?").charAt(0).toUpperCase()}
                            </span>
                            {u.name}
                          </span>
                        ))}
                        {c.assigned_users.length > 4 && (
                          <span className="inline-flex items-center bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 text-[10px]">
                            +{c.assigned_users.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <Badge variant="outline" className={`${statusColor(c.status)} text-[10px] rounded-full`}>
                      {c.status || "Active"}
                    </Badge>
                    <CaretRight size={14} className="text-slate-400" />
                  </div>
                </button>
              ); })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
